import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';

/**
 * EVERY BUTTON THAT STARTS SOMETHING SHOWS IT HAS (Freddi, 2026-10-08: "loading
 * spinner on all buttons when page is loading after submitting or saving …
 * EVERYTHING — should be global rule").
 *
 * One component, so the rule is one rule. A button is busy when its owner says
 * so (`busy` — a send, a read), when the action it ran returns a promise (busy
 * until it settles), or when the tap leaves the page (`leaves`, or a same-tab
 * link — busy until the next page replaces this one). Busy keeps the colour,
 * the label and the size; only the icon slot changes, and only after a beat
 * (customer.css, `.is-busy .cp-glyph`), so an answer that lands quickly changes
 * nothing. A busy button ignores a second tap: two taps are two sends.
 */

export type ClickOutcome = 'until-settled' | 'until-gone' | 'idle';

/**
 * What a tap leaves the button doing. Pure, so the rule is pinned without a DOM.
 * `plain` is false for a modifier- or middle-click: on a link that opens a new
 * tab and this page goes nowhere, so the button must stay live (PR #242 review).
 */
export function clickOutcome(result: unknown, leaves: boolean, plain = true): ClickOutcome {
  if (result && typeof (result as { then?: unknown }).then === 'function') return 'until-settled';
  return leaves && plain ? 'until-gone' : 'idle';
}

/**
 * Run `done` when a promise settles either way. `.finally()` hands back a new
 * promise that rejects with the original error — nothing awaits it, so a failed
 * action surfaced as an unhandled rejection (PR #242 review).
 */
export function settle(p: PromiseLike<unknown>, done: () => void): Promise<void> {
  return Promise.resolve(p).then(done, done);
}

/** The icon's slot, and while busy the spinner over it. */
export function ButtonGlyph({ icon, busy = false }: { icon?: ReactNode; busy?: boolean }) {
  if (!icon && !busy) return null;
  return (
    <span className="cp-glyph">
      <span className="cp-glyph-ico">{icon}</span>
      {busy && <span className="cp-spin" aria-hidden="true" />}
    </span>
  );
}

export interface PendingButtonProps {
  className: string;
  icon?: ReactNode;
  children: ReactNode;
  /** The owner's own busy — a send in flight, a read out. */
  busy?: boolean;
  /** The tap takes the customer to another page; stay busy until it does. */
  leaves?: boolean;
  /** A same-tab link: following it leaves the page. */
  href?: string;
  /** Opens in a new tab — nothing here waits on it. */
  external?: boolean;
  onClick?: () => unknown;
  type?: 'button' | 'submit';
  disabled?: boolean;
  title?: string;
}

export function PendingButton({ className, icon, children, busy = false, leaves = false, href, external = false, onClick, type = 'button', disabled = false, title }: PendingButtonProps) {
  const [pending, setPending] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const isBusy = busy || pending;
  const cls = className + (isBusy ? ' is-busy' : '');

  const run = (e: MouseEvent) => {
    if (isBusy) { e.preventDefault(); return; }
    const result = onClick?.();
    const plain = e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
    const outcome = clickOutcome(result, leaves || (!!href && !external), plain);
    if (outcome === 'until-gone') setPending(true);
    if (outcome === 'until-settled') {
      setPending(true);
      void settle(result as PromiseLike<unknown>, () => { if (alive.current) setPending(false); });
    }
  };

  const inner = (
    <>
      <ButtonGlyph icon={icon} busy={isBusy} />
      {children}
    </>
  );

  if (href && !disabled) {
    return (
      <a
        className={cls}
        href={href}
        title={title}
        aria-disabled={isBusy ? 'true' : undefined}
        aria-busy={isBusy ? 'true' : undefined}
        onClick={run}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {inner}
      </a>
    );
  }
  return (
    // A submit button's work is the form's; its owner passes `busy`.
    <button type={type} className={cls} disabled={disabled || isBusy} aria-busy={isBusy ? 'true' : undefined} title={title} onClick={type === 'submit' ? undefined : run}>
      {inner}
    </button>
  );
}
